import type { SchemaProperty } from 'types/schema';
import type { RenderMode, RendererMap, RendererRef } from 'types/aspect';
import { hasAdapter } from '../composition-host/adapterRegistry';

/**
 * Whether a resolved whole-aspect `catalog` ref actually names a registered adapter. A
 * `composition` ref's mountability can't be known synchronously (it needs a network fetch), so it
 * always passes here — `WholeAspectRenderer` mounts it and reactively falls back to flattened rows
 * if the fetch settles unresolved (EAINT-855 review pass 2, H-3, second half). An unregistered
 * catalog name must not form a group: mounting it degrades to the terminal adapter (`TextField`/
 * `GenericDisplay`) rendering the aspect's raw VALUE BAG OBJECT, not a "no value" affordance — an
 * `[object Object]` box, which is exactly the "never blank" edge case the spec forbids
 * (specs/aspect-default-components.md §Edge cases).
 *
 * Deliberately reads the registry (`adapterRegistry.ts`, a plain `Map`) WITHOUT importing
 * `registerBuiltInAdapters` itself: that module pulls in the full organism-adapter graph
 * (`AtlasComponent` and everything it renders), which loops back through the design-system barrel
 * into this package's own property-rendering modules. Importing the registration module here made
 * this file's load position in the graph observable: entering via `groupPropertiesByAspect.spec.ts`
 * first raced that cycle mid-init and crashed with "Cannot access '_organismAdapters' before
 * initialization". Since Phase 6 no caller has to register anything first — `hasAdapter` itself
 * registers the built-ins lazily on the first lookup that misses (`adapterRegistry.ts`'s
 * `ensureBuiltIns`), so a bare unit test sees the same registry a full render tree does.
 */
function isCatalogRefMountable(ref: RendererRef): boolean {
  if (ref.origin !== 'catalog') return true;
  if (hasAdapter(ref.name)) return true;
  // eslint-disable-next-line no-console
  console.warn(
    `[groupPropertiesByAspect] whole-aspect catalog ref "${ref.name}" is not a registered adapter — falling back to flattened rows`
  );
  return false;
}

/**
 * Resolves a whole-aspect `RendererMap` (EAINT-855 spec §Resolution chain, whole aspect) —
 * distinct from `resolveRenderer` (which is per-property and has legacy-compat + type-default
 * steps that don't apply at the aspect level). `hasAnyValue` selects `empty` over `mode` per the
 * spec ("empty applies when no contributed property has a value"); `compact`/`empty` degrade to
 * `view`. Returns `undefined` when nothing resolves — the caller then falls back to rendering the
 * group's properties as ordinary flattened rows.
 */
export function resolveWholeAspectRenderer(map: RendererMap | undefined, mode: RenderMode, hasAnyValue: boolean): RendererRef | undefined {
  if (!map) return undefined;
  const effectiveMode: RenderMode = hasAnyValue ? mode : 'empty';
  const explicit = map[effectiveMode];
  if (explicit) return explicit;
  if (effectiveMode === 'compact' || effectiveMode === 'empty') return map.view;
  return undefined;
}

export interface AspectGroup {
  aspectSystemName: string;
  ref: RendererRef;
  properties: SchemaProperty[];
  /**
   * The mode this group's `ref` was resolved at, and the mode the caller MUST render it at —
   * returned rather than recomputed so resolution and rendering can never disagree. Equal to the
   * requested `mode` except when the ADR-022 write gate forces the group read-only (see
   * `writeBlocked`).
   */
  mode: RenderMode;
  /**
   * True when at least one contributed property is write-blocked for this user, which degrades the
   * whole group to `view`. A whole-aspect renderer edits the aspect as ONE unit through an
   * author-supplied component, so the host cannot gate an individual key inside it the way the
   * flattened path does per row — and an aspect declaring `components.aspect.edit` is asserting
   * "edit these together". If the user may not write part of it, editing it as a unit is not
   * authorized, so the group renders read-only instead of offering an affordance whose writes
   * would be dropped (or rejected with a 403 that discards the entire sphere update).
   */
  writeBlocked: boolean;
}

/**
 * One render-order unit: either a whole-aspect group or a contiguous run of flattened rows.
 * `SphereProperties` renders `GroupedProperties.segments` (NOT `aspectGroups` then
 * `flatProperties` back to back) — those two arrays alone lose each group's position RELATIVE TO
 * the flat rows around it, because splitting a single ordered walk into two arrays and rendering
 * "all groups, then all flats" collapses whatever interleaving existed in the source schema
 * (EAINT-855 review pass 2, M-8).
 */
export type AspectSegment = { type: 'group'; group: AspectGroup } | { type: 'flat'; properties: SchemaProperty[] };

export interface GroupedProperties {
  aspectGroups: AspectGroup[];
  flatProperties: SchemaProperty[];
  segments: AspectSegment[];
}

/**
 * Partitions a flat property list into whole-aspect groups (pulled out, to be rendered once via
 * their declared `components.aspect` renderer) and the remainder (rendered as ordinary flattened
 * rows) — the whole-aspect grouping step `SphereProperties` mounts (EAINT-855 SC1). A property
 * only joins a group when its `aspect_system_name` names an aspect present in
 * `aspectComponentsMap` AND that aspect's `RendererMap` actually resolves for `mode` — otherwise
 * it (and the rest of its aspect's properties, if none of them resolve either) stays flat.
 *
 * `writeBlockedProps` carries the ADR-022 write gate (the set the flattened path enforces per row
 * via `effectiveEditable`). An aspect with any write-blocked contributed property is resolved and
 * returned at `view`, never `edit` — see `AspectGroup.writeBlocked`.
 */
export function groupPropertiesByAspect(
  properties: SchemaProperty[],
  aspectComponentsMap: Record<string, RendererMap> | undefined,
  mode: RenderMode,
  values: Record<string, any>,
  writeBlockedProps?: Set<string>
): GroupedProperties {
  // Which aspects even have a whole-aspect renderer declared, and which of their properties
  // would join the group. Order-preserving, so a group is later emitted at its FIRST property's
  // position rather than appended.
  const candidatesByAspect = new Map<string, SchemaProperty[]>();
  for (const prop of properties) {
    const aspectName = prop.aspect_system_name;
    if (!aspectName || !aspectComponentsMap?.[aspectName]) continue;
    if (!candidatesByAspect.has(aspectName)) candidatesByAspect.set(aspectName, []);
    candidatesByAspect.get(aspectName)!.push(prop);
  }

  // A declared map still has to RESOLVE for this mode + emptiness. One that doesn't means the
  // aspect's properties render as ordinary rows.
  const resolvedByAspect = new Map<string, { ref: RendererRef; mode: RenderMode; writeBlocked: boolean }>();
  for (const [aspectSystemName, groupProps] of candidatesByAspect.entries()) {
    const hasAnyValue = groupProps.some((p) => values[p.name] != null);
    // The write gate is applied BEFORE resolution, not after: an aspect may declare different refs
    // for `edit` and `view`, so resolving at `edit` and then rendering at `view` would mount the
    // edit component in a read-only host.
    const writeBlocked = groupProps.some((p) => writeBlockedProps?.has(p.name) ?? false);
    const effectiveMode: RenderMode = writeBlocked && mode === 'edit' ? 'view' : mode;
    const ref = resolveWholeAspectRenderer(aspectComponentsMap?.[aspectSystemName], effectiveMode, hasAnyValue);
    if (ref && isCatalogRefMountable(ref)) resolvedByAspect.set(aspectSystemName, { ref, mode: effectiveMode, writeBlocked });
  }

  // Single walk so a NON-resolving aspect's properties keep their original position within
  // `flatProperties`. Partitioning in two passes and appending them afterwards would sink them to
  // the bottom of the grid — and since resolution depends on mode and emptiness, the same schema
  // would visibly reorder itself between view and edit, or the moment a value was cleared.
  // Note this does NOT interleave groups with flat rows: the caller renders all `aspectGroups`
  // before all `flatProperties`, so a group's position relative to ungrouped rows is not preserved.
  const aspectGroups: AspectGroup[] = [];
  const flatProperties: SchemaProperty[] = [];
  const segments: AspectSegment[] = [];
  let currentFlatRun: SchemaProperty[] = [];
  const flushFlatRun = () => {
    if (currentFlatRun.length === 0) return;
    segments.push({ type: 'flat', properties: currentFlatRun });
    currentFlatRun = [];
  };
  const emitted = new Set<string>();
  for (const prop of properties) {
    const aspectName = prop.aspect_system_name;
    const resolved = aspectName ? resolvedByAspect.get(aspectName) : undefined;
    if (aspectName && resolved) {
      if (!emitted.has(aspectName)) {
        emitted.add(aspectName);
        const group: AspectGroup = {
          aspectSystemName: aspectName,
          ref: resolved.ref,
          properties: candidatesByAspect.get(aspectName)!,
          mode: resolved.mode,
          writeBlocked: resolved.writeBlocked,
        };
        aspectGroups.push(group);
        flushFlatRun();
        segments.push({ type: 'group', group });
      }
      continue;
    }
    flatProperties.push(prop);
    currentFlatRun.push(prop);
  }
  flushFlatRun();

  return { aspectGroups, flatProperties, segments };
}
